import { Component } from 'react';
import styled from 'styled-components';
import { DiffMode } from '@ardoq/api-types';
import { LegendItem } from './LegendItem';
import {
  ActiveConditionalFormattingForLegend,
  ConditionalFormattingInfo,
  FormattingSectionHeadings,
  LegendItemModel,
  ViewLegendProps,
  ViewLegendReferenceType,
  ViewLegendSection,
  ViewLegendState,
} from './types';
import { ViewLegendRow } from './ViewLegendRow';
import { VisualDiffLegends } from './VisualDiffLegends';
import { s16, s8 } from '@ardoq/design-tokens';
import { text2Bold } from '@ardoq/typography';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { ShapeIcon } from './ShapeIcon';
import { CUSTOM_SVG_ICON_SELECTOR } from '@ardoq/icons';
import { toDrawableShapeName } from '@ardoq/data-model';
import { isTruthy } from '@ardoq/common-helpers';
import { ViewLegendItemLabel } from './ViewLegendItemLabel';
import { ViewLegendReference } from './ViewLegendReference';
import { ViewLegendItem } from './ViewLegendItem';
import { getLightenedColor } from '@ardoq/color-helpers';
import { NO_EXPORT_CLASS_NAME } from '@ardoq/global-consts';

const referencesLegendSection = (
  referenceTypes: ViewLegendReferenceType[]
) => ({
  heading: null,
  items: referenceTypes.map(referenceType => ({
    getShape: () => <ViewLegendReference referenceType={referenceType} />,
  })),
});
const ViewLegendSectionElement = styled.div`
  &:not(:first-child) {
    margin-top: ${s16};
  }
  &:not(:last-child) {
    margin-bottom: ${s8};
  }
`;
interface ViewLegendSectionComponentProperties {
  section: ViewLegendSection;
  itemClickTrackingFunction?: () => void;
}
const ViewLegendSectionComponent = ({
  section: { heading, items, selectableItems, setSelectedItems },
  itemClickTrackingFunction,
}: ViewLegendSectionComponentProperties) => {
  const selectedItems = items.filter(item => item.isSelected);
  return (
    <ViewLegendSectionElement>
      {heading && (
        <ViewLegendSectionSubheading>{heading}</ViewLegendSectionSubheading>
      )}
      {items.map((item, itemIndex) => {
        const {
          color,
          borderColor,
          label,
          labelText,
          getShape,
          style,
          onMouseOver,
          onMouseOut,
          isSelected,
        } = item;
        return (
          <LegendItem
            key={`${labelText ?? label}_${itemIndex}`}
            color={color}
            borderColor={borderColor}
            description={label}
            descriptionText={labelText}
            getShape={getShape}
            style={style}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            isSelected={isSelected}
            isSelectable={Boolean(selectableItems)}
            setSelected={value => {
              const newSelectedItems = value
                ? Array.from(new Set([...selectedItems, item]))
                : selectedItems.filter(selectedItem => selectedItem !== item);
              setSelectedItems?.(newSelectedItems);
            }}
            itemClickTrackingFunction={() => itemClickTrackingFunction?.()}
          />
        );
      })}
    </ViewLegendSectionElement>
  );
};

const showLegendFormatting = ({
  components: compFormatting,
  references: refFormatting,
  tags: tagFormatting,
}: ActiveConditionalFormattingForLegend) =>
  compFormatting.length > 0 ||
  refFormatting.length > 0 ||
  tagFormatting.length > 0;

const formattingItemToViewLegendItem = (
  info: ConditionalFormattingInfo,
  useAsBackgroundStyle?: boolean
): LegendItemModel => ({
  id: info.id,
  color:
    info.color && useAsBackgroundStyle
      ? getLightenedColor(info.color)
      : info.color,
  borderColor: info.color && useAsBackgroundStyle ? info.color : undefined,
  label: info.filterDescriptionJSX ?? info.filterDescription,
  // When using JSX, provide plain text for tooltip
  labelText: info.filterDescription,
});
interface FormattingInfo {
  heading: string;
  items: ConditionalFormattingInfo[];
}
const formattingToViewLegendSection = (
  { heading, items }: FormattingInfo,
  useAsBackgroundStyle?: boolean
) =>
  !items.length
    ? null
    : {
        heading,
        items: items.map(item =>
          formattingItemToViewLegendItem(item, useAsBackgroundStyle)
        ),
      };
export const formattingSections = (
  activeConditionalFormatting: ActiveConditionalFormattingForLegend,
  useAsBackgroundStyle?: boolean,
  headings?: FormattingSectionHeadings
) => {
  const {
    components: componentFormatting,
    references: referenceFormatting,
    tags: tagFormatting,
  } = activeConditionalFormatting;
  if (!showLegendFormatting(activeConditionalFormatting)) {
    return null;
  }
  const {
    components = 'Components',
    references = 'References',
    tags = 'Tags',
  } = headings ?? {};
  return [
    { heading: components, items: componentFormatting },
    { heading: references, items: referenceFormatting },
    { heading: tags, items: tagFormatting },
  ]
    .map(formatting =>
      formattingToViewLegendSection(formatting, useAsBackgroundStyle)
    )
    .filter(isTruthy);
};

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  font-size: 18px;
  max-height: 20px;
  user-select: none;

  > svg {
    height: 26px;
  }

  > ${CUSTOM_SVG_ICON_SELECTOR}
    // Needed for ShapeIcon rendering
    > svg {
    max-height: 26px;
    max-width: 32px;
  }
`;

const ViewLegendSectionSubheading = styled.div`
  margin: ${s8} 0;
  ${text2Bold};
`;

export class ViewLegend extends Component<ViewLegendProps, ViewLegendState> {
  constructor(props: ViewLegendProps) {
    super(props);

    this.state = this.props.collapsedPreferences || {
      connectedPeopleCollapsed: true,
      componentsCollapsed: false,
      referencesCollapsed: false,
      formattingCollapsed: false,
      visualDiffCollapsed: false,
      collapsedAdditionalRowIds: new Set(),
    };
  }
  toggleComponentsCollapsed = () =>
    this.setState({ componentsCollapsed: !this.state.componentsCollapsed });
  toggleReferencesCollapsed = () =>
    this.setState({ referencesCollapsed: !this.state.referencesCollapsed });
  toggleConnectedPeopleCollapsed = () =>
    this.setState({
      connectedPeopleCollapsed: !this.state.connectedPeopleCollapsed,
    });
  toggleFormattingCollapsed = () =>
    this.setState({ formattingCollapsed: !this.state.formattingCollapsed });
  toggleAdditionalRowCollapsed = (id: string) => {
    const collapsedAdditionalRowIds = new Set(
      this.state.collapsedAdditionalRowIds
    );
    const isCollapsed = collapsedAdditionalRowIds.has(id);
    if (isCollapsed) {
      collapsedAdditionalRowIds.delete(id);
    } else {
      collapsedAdditionalRowIds.add(id);
    }
    this.setState({ collapsedAdditionalRowIds });
  };
  toggleVisualDiffCollapsed = () =>
    this.setState({ visualDiffCollapsed: !this.state.visualDiffCollapsed });
  render() {
    const {
      connectedPeopleLegend,
      componentTypes = [],
      referenceTypes = [],
      activeConditionalFormatting = {
        components: [],
        references: [],
        tags: [],
      },
      activeDiffMode,
      itemClickTrackingFunction,
      connectedPeopleLegendOnClick,
      labels,
    } = this.props;

    const {
      visualDiff = 'Visual Diff',
      connectedPeople = 'Connected People',
      components = 'Components',
      references = 'References',
      conditionalFormatting = 'Conditional Formatting',
      formattingComponentsHeading,
      formattingReferencesHeading,
      formattingTagsHeading,
      visualDiffLabels,
    } = labels ?? {};

    const {
      connectedPeopleCollapsed,
      componentsCollapsed,
      referencesCollapsed,
      formattingCollapsed,
      visualDiffCollapsed,
    } = this.state;

    /** UX requirement: when in diff mode, don't show components, because the colors in the components legend will not match the ones in the view. */
    const areDiffColorsActive = activeDiffMode === DiffMode.DIFF;
    const showComponents = componentTypes.length > 0 && !areDiffColorsActive;
    const showReferences = referenceTypes.length > 0 && !areDiffColorsActive;
    return (
      <>
        {activeDiffMode && (
          <ViewLegendRow
            collapsed={visualDiffCollapsed}
            toggleRowCollapsed={this.toggleVisualDiffCollapsed}
            label={visualDiff}
          >
            <VisualDiffLegends
              activeDiffMode={activeDiffMode}
              labels={visualDiffLabels}
            />
          </ViewLegendRow>
        )}

        {connectedPeopleLegend && (
          <ViewLegendRow
            collapsed={connectedPeopleCollapsed}
            toggleRowCollapsed={() => {
              this.toggleConnectedPeopleCollapsed();
              connectedPeopleLegendOnClick?.(
                this.state.connectedPeopleCollapsed
              );
            }}
            label={connectedPeople}
            className={NO_EXPORT_CLASS_NAME}
          >
            {connectedPeopleLegend}
          </ViewLegendRow>
        )}

        {showComponents && (
          <ViewLegendRow
            collapsed={componentsCollapsed}
            toggleRowCollapsed={this.toggleComponentsCollapsed}
            label={components}
          >
            {componentTypes.map((componentType, index) => (
              <ViewLegendItem key={index}>
                <IconContainer>
                  {this.props.displayShapesInsteadOfIcons &&
                  !componentType.representationData?.isImage ? (
                    <ShapeIcon
                      shapeName={toDrawableShapeName(
                        componentType.representationData?.shapeName
                      )}
                      color={componentType.color}
                    />
                  ) : (
                    <ComponentRepresentation
                      isImage={componentType.representationData.isImage}
                      value={componentType.representationData.value}
                      icon={componentType.representationData.icon}
                      style={{ fontSize: 21 }}
                    />
                  )}
                </IconContainer>
                <ViewLegendItemLabel>{componentType.label}</ViewLegendItemLabel>
              </ViewLegendItem>
            ))}
          </ViewLegendRow>
        )}

        {showReferences && (
          <ViewLegendRow
            collapsed={referencesCollapsed}
            toggleRowCollapsed={this.toggleReferencesCollapsed}
            label={references}
          >
            <ViewLegendSectionComponent
              section={referencesLegendSection(referenceTypes)}
            />
          </ViewLegendRow>
        )}

        {showLegendFormatting(activeConditionalFormatting) && (
          <ViewLegendRow
            collapsed={formattingCollapsed}
            toggleRowCollapsed={this.toggleFormattingCollapsed}
            label={conditionalFormatting}
          >
            {formattingSections(activeConditionalFormatting, undefined, {
              components: formattingComponentsHeading,
              references: formattingReferencesHeading,
              tags: formattingTagsHeading,
            })?.map((section, index) => (
              <ViewLegendSectionComponent
                key={`${index}_${section.heading}_${section.items.length}`}
                section={section}
              />
            ))}
          </ViewLegendRow>
        )}
        {this.props.additionalRows?.map(({ id, title, sections }) => (
          <ViewLegendRow
            key={`additionalRows_${id}`}
            collapsed={this.state.collapsedAdditionalRowIds.has(id)}
            label={title}
            toggleRowCollapsed={() => this.toggleAdditionalRowCollapsed(id)}
          >
            {sections.map((section, index) => (
              <ViewLegendSectionComponent
                key={`${index}_${section.heading}_${section.items.length}`}
                section={section}
                itemClickTrackingFunction={() =>
                  itemClickTrackingFunction?.(id)
                }
              />
            ))}
          </ViewLegendRow>
        ))}
      </>
    );
  }
}
